// Control Frame Characteristics
export const FLATNESS_SYMBOL = '⏥';
export const CYLINDRICITY_SYMBOL = '⌭';
export const PROFILE_OF_A_LINE_SYMBOL = '⌒';
export const PROFILE_OF_A_SURFACE_SYMBOL = '⌓';
export const PERPENDICULARITY_SYMBOL = '⏊';
export const ANGULARITY_SYMBOL = '∠';
export const POSITION_SYMBOL = '⌖';
export const CONCENTRICITY_SYMBOL = '◎';
export const RUNOUT_SYMBOL = '↗';
export const TOTAL_RUNOUT_SYMBOL = '⌰';
export const PARALLELISM_SYMBOL = '⫽';
export const SYMMETRY_SYMBOL = '⌯';
export const CIRCULARITY_SYMBOL = '○';
export const STRAIGHTNESS_SYMBOL = '⏤';

export const CONTROL_FRAME_CHARACTERISTICS = {
  [FLATNESS_SYMBOL]: 'Flatness',
  [CYLINDRICITY_SYMBOL]: 'Cylindricity',
  [PROFILE_OF_A_LINE_SYMBOL]: 'Profile of a Line',
  [PROFILE_OF_A_SURFACE_SYMBOL]: 'Profile of a Surface',
  [PERPENDICULARITY_SYMBOL]: 'Perpendicularity',
  [ANGULARITY_SYMBOL]: 'Angularity',
  [POSITION_SYMBOL]: 'Position',
  [CONCENTRICITY_SYMBOL]: 'Concentricity',
  [RUNOUT_SYMBOL]: 'Runout',
  [TOTAL_RUNOUT_SYMBOL]: 'Total Runout',
  [PARALLELISM_SYMBOL]: 'Parallelism',
  [SYMMETRY_SYMBOL]: 'Symmetry',
  [CIRCULARITY_SYMBOL]: 'Circularity',
  [STRAIGHTNESS_SYMBOL]: 'Straightness',
} as const;

export type ControlFrameCharacteristic =
  | typeof FLATNESS_SYMBOL
  | typeof CYLINDRICITY_SYMBOL
  | typeof PROFILE_OF_A_LINE_SYMBOL
  | typeof PROFILE_OF_A_SURFACE_SYMBOL
  | typeof PERPENDICULARITY_SYMBOL
  | typeof ANGULARITY_SYMBOL
  | typeof POSITION_SYMBOL
  | typeof CONCENTRICITY_SYMBOL
  | typeof RUNOUT_SYMBOL
  | typeof TOTAL_RUNOUT_SYMBOL
  | typeof PARALLELISM_SYMBOL
  | typeof SYMMETRY_SYMBOL
  | typeof CIRCULARITY_SYMBOL
  | typeof STRAIGHTNESS_SYMBOL;

export const RUNES_CALLOUT_TYPES = {
  DIM: 'dim',
  HOLE: 'hole',
  COUNTERBORE: 'counterbore',
  COUNTERSINK: 'countersink',
  DATUM: 'datum',
  CONTROL_FRAME: 'control_frame',
  COMPOSITE_CONTROL_FRAME: 'composite_control_frame',
  SURFACE_FINISH: 'surface_finish',
  BEND_LINE: 'bend_line',
  THREAD: 'thread',
  GENERAL_TOLERANCES: 'general_tolerances',
  FLAG_NOTE: 'flag_note',
  CAPTION: 'caption',
  WELD: 'weld',
  TABLE: 'table',
  CHAMFER: 'chamfer',
} as const;
export type RunesCalloutType =
  (typeof RUNES_CALLOUT_TYPES)[keyof typeof RUNES_CALLOUT_TYPES];

export type Units = 'mm' | 'in' | 'deg';

// PartBotLocation type for callout positioning
export type PartBotLocation = [number, number, number, number]; // X1, Y1, X2, Y2 from bottom left origin

export type ConfidenceScore = number;

// Region types
export const RUNES_REGION_TYPES = {
  TITLE_BLOCK: 'title_block',
  NOTES_LIST: 'notes_list',
  VIEW_3D: 'view_3d',
  VIEW_2D: 'view_2d',
} as const;
export type RunesRegionType =
  (typeof RUNES_REGION_TYPES)[keyof typeof RUNES_REGION_TYPES];

export type TitleBlock = {
  regionType: typeof RUNES_REGION_TYPES.TITLE_BLOCK;
};

export type NotesList = {
  regionType: typeof RUNES_REGION_TYPES.NOTES_LIST;
};

export type View3D = {
  regionType: typeof RUNES_REGION_TYPES.VIEW_3D;
};

export type View2D = {
  regionType: typeof RUNES_REGION_TYPES.VIEW_2D;
};

export type RunesRegion = {
  region: TitleBlock | NotesList | View3D | View2D;
  loc: PartBotLocation;
  score: ConfidenceScore;
};

export type Value = {
  value: number;
  units?: Units;
  altValue?: number;
  altUnits?: Units;
  precision?: number;
};

export const TOLERANCE_TYPES = {
  LIMIT: 'limit',
  SYMMETRIC: 'symmetric',
  ASYMMETRIC: 'asymmetric',
} as const;
export type ToleranceType =
  (typeof TOLERANCE_TYPES)[keyof typeof TOLERANCE_TYPES];

export type Tolerance = {
  tolType: ToleranceType;
  upper?: Value;
  lower?: Value;
};

export type Measurement = {
  value: Value;
  tol?: Tolerance;
};

export const DIMENSION_TYPES = {
  LINEAR: 'linear',
  ANGULAR: 'angular',
  DIAMETER: 'diameter',
  RADIUS: 'radius',
} as const;
export type DimensionType =
  (typeof DIMENSION_TYPES)[keyof typeof DIMENSION_TYPES];

export const ENCLOSURE_SHAPES = {
  CIRCLE: 'circle',
  BOX: 'box',
  OBROUND: 'obround',
  PENTAGON: 'pentagon',
  HEXAGON: 'hexagon',
  TRIANGLE: 'triangle',
  PARENTHESES: 'parentheses',
  OTHER: 'other',
} as const;
export type EnclosureShape =
  (typeof ENCLOSURE_SHAPES)[keyof typeof ENCLOSURE_SHAPES];

export const BEND_DIRECTIONS = {
  UP: 'up',
  DOWN: 'down',
} as const;
export type BendDirection =
  (typeof BEND_DIRECTIONS)[keyof typeof BEND_DIRECTIONS];

export const THREAD_SYSTEMS = {
  METRIC: 'metric',
  NPT: 'npt',
  UNC: 'unc',
  UNF: 'unf',
  UNEF: 'unef',
} as const;
export type ThreadSystem = (typeof THREAD_SYSTEMS)[keyof typeof THREAD_SYSTEMS];

export const HANDEDNESS = {
  LH: 'LH',
  RH: 'RH',
} as const;
export type Handedness = (typeof HANDEDNESS)[keyof typeof HANDEDNESS];

export const MATERIAL_REMOVAL_TYPES = {
  UNSPECIFIED: 'unspecified',
  REQUIRED: 'required',
  PROHIBITED: 'prohibited',
} as const;
export type MaterialRemovalType =
  (typeof MATERIAL_REMOVAL_TYPES)[keyof typeof MATERIAL_REMOVAL_TYPES];

export const VIEW_TYPES = {
  PROFILE_VIEW: 'profile_view',
  SECTION_VIEW: 'section_view',
  ISOMETRIC_VIEW: 'isometric_view',
  DETAIL_VIEW: 'detail_view',
} as const;
export type ViewType = (typeof VIEW_TYPES)[keyof typeof VIEW_TYPES];

export const ELEMENT_CLASSES = {
  TITLE_BLOCK: 'title_block',
  NOTES_LIST: 'notes_list',
  VIEW_3D: 'view_3d',
  VIEW_2D: 'view_2d',
  CAPTION: 'caption',
  TABLE: 'table',
  WELD: 'weld',
  GENERAL_TOLERANCES: 'general_tolerances',
  CALLOUT_REGION: 'callout_region',
} as const;
export type ElementClass =
  (typeof ELEMENT_CLASSES)[keyof typeof ELEMENT_CLASSES];

export const RUNES_API_VERSIONS = {
  V1_0_0: '1.0.0',
  V2_0_0: '2.0.0',
} as const;
export type RunesApiVersion =
  (typeof RUNES_API_VERSIONS)[keyof typeof RUNES_API_VERSIONS];

export const ORIENTATIONS = {
  NEAR_SIDE: 'near_side',
  FAR_SIDE: 'far_side',
  BOTH_SIDES: 'both_sides',
} as const;
export type Orientation = (typeof ORIENTATIONS)[keyof typeof ORIENTATIONS];

// Helper types
export type SpecificDecimalTolerance = {
  zeroPlaces?: number;
  onePlaces?: number;
  twoPlaces?: number;
  threePlaces?: number;
  fourPlaces?: number;
};

export type RawDetection = [ElementClass, PartBotLocation, ConfidenceScore];

export type Dimension = {
  calloutType: typeof RUNES_CALLOUT_TYPES.DIM;
  dimType: DimensionType;
  mult?: number;
  meas: Measurement;
  isOrigin?: boolean;
  isBasic?: boolean;
  isCtq?: boolean;
  isRef?: boolean;
  enclosureShape?: EnclosureShape;
  isTyp?: boolean;
};

export const DEPTH_TYPES = {
  THRU: 'thru',
  DEPTH: 'depth',
} as const;
export type DepthType = (typeof DEPTH_TYPES)[keyof typeof DEPTH_TYPES];

export type Thread = {
  calloutType: typeof RUNES_CALLOUT_TYPES.THREAD;
  threadSystem: ThreadSystem;
  size: string;
  pitch?: string;
  threadDepth?: string;
  toleranceClass?: string;
  handedness?: Handedness;
  rawThreadStr: string;
};

export type Hole = {
  calloutType: typeof RUNES_CALLOUT_TYPES.HOLE;
  mult?: number;
  dia?: Measurement;
  depthType?: DepthType;
  depth?: Measurement;
  thread?: Thread;
  isTyp?: boolean;
};

export type Countersink = {
  calloutType: typeof RUNES_CALLOUT_TYPES.COUNTERSINK;
  mult?: number;
  dia: Measurement;
  angle?: Measurement;
  orientation?: Orientation;
  isTyp?: boolean;
};

export type Counterbore = {
  calloutType: typeof RUNES_CALLOUT_TYPES.COUNTERBORE;
  mult?: number;
  dia: Measurement;
  depthType?: DepthType;
  depth?: Measurement;
  thread?: Thread;
  orientation?: Orientation;
  isTyp?: boolean;
};

export const MATERIAL_CONDITIONS = {
  MMC: 'mmc',
  LMC: 'lmc',
} as const;
export type MaterialCondition =
  (typeof MATERIAL_CONDITIONS)[keyof typeof MATERIAL_CONDITIONS];

export type Datum = {
  calloutType: typeof RUNES_CALLOUT_TYPES.DATUM;
};

export type DatumReference = {
  datum: string;
  matCon?: MaterialCondition;
};

export type FeatureControlFrame = {
  calloutType: typeof RUNES_CALLOUT_TYPES.CONTROL_FRAME;
  characteristic: ControlFrameCharacteristic;
  dimType: DimensionType;
  value: Value;
  matCon?: MaterialCondition;
  datums: DatumReference[];
};

export type CompositeControlFrame = {
  calloutType: typeof RUNES_CALLOUT_TYPES.COMPOSITE_CONTROL_FRAME;
  controlFrames: FeatureControlFrame[];
  rawTexts: string[];
  subLocs: number[][];
};

export type BendLine = {
  calloutType: typeof RUNES_CALLOUT_TYPES.BEND_LINE;
  direction: BendDirection;
  angle: Measurement;
  radius?: Measurement;
};

export type Chamfer = {
  calloutType: typeof RUNES_CALLOUT_TYPES.CHAMFER;
  mult?: number;
  legLength: Measurement;
  angle?: Measurement;
  secondLegLength?: Measurement;
  isTyp?: boolean;
};

export type SurfaceFinish = {
  calloutType: typeof RUNES_CALLOUT_TYPES.SURFACE_FINISH;
  materialRemovalType: MaterialRemovalType;
  raMean?: number;
  raRange?: [number, number];
  raClass?: string;
};

export type GeneralTolerances = {
  calloutType: typeof RUNES_CALLOUT_TYPES.GENERAL_TOLERANCES;
  angleTolerance?: number | SpecificDecimalTolerance;
  angleToleranceUnits?: Units;
  metricTolerance?: number | SpecificDecimalTolerance;
  metricToleranceUnits?: Units;
  usTolerance?: number | SpecificDecimalTolerance;
  usToleranceUnits?: Units;
  generalRoughness?: SurfaceFinish;
  toleranceStandard?: string;
};

export type FlagNote = {
  calloutType: typeof RUNES_CALLOUT_TYPES.FLAG_NOTE;
  listMarkerValue: string;
  enclosureShape: EnclosureShape;
};

export type Caption = {
  calloutType: typeof RUNES_CALLOUT_TYPES.CAPTION;
  viewType?: ViewType;
  region?: RunesRegion;
};

export type Weld = {
  calloutType: typeof RUNES_CALLOUT_TYPES.WELD;
};

export type RunesTable = {
  calloutType: typeof RUNES_CALLOUT_TYPES.TABLE;
};

export type CalloutType =
  | Dimension
  | Hole
  | Countersink
  | Counterbore
  | Datum
  | FeatureControlFrame
  | CompositeControlFrame
  | Thread
  | BendLine
  | Chamfer
  | SurfaceFinish
  | GeneralTolerances
  | FlagNote
  | Caption
  | Weld
  | RunesTable;

export type RunesCallout = {
  callout: CalloutType;
  rawText: string;
  loc: PartBotLocation;
  desc?: string | null;
  anomalous?: boolean;
};

export type CalloutGroup = {
  callouts: number[];
  loc: PartBotLocation;
};

export type RunesResponse = {
  callouts: RunesCallout[];
  calloutGroups: CalloutGroup[];
  regions: RunesRegion[];
  rawDetections: RawDetection[];
  version: string;
  stage1Ms: number;
  stage1GpuMs: number;
  stage2Ms: number;
};
